"use client";

import { useEffect } from "react";
import { creator, product, social } from "@/data/content";
import { clizsec, scamatrix } from "@/data/discoverability";

const DOCKED_DEVTOOLS_THRESHOLD_PX = 160;

const ASCII_LOGO = String.raw`
  ____                  _    __  __       _      _
 / ___|  ___ __ _ _ __ | |_ |  \\/  | __ _| |_ __(_) __ _ _ __
 \\___ \\ / __/ _\` | '_ \\| __| | |\\/| |/ _\` | __/ _ \\ |/ _\` | '_ \\
  ___) | (_| (_| | | | | |_  | |  | | (_| | ||  __/ | (_| | | | |
 |____/ \\___\\__,_|_| |_|\\__| |_|  |_|\\__,_|\\__\\___|_|\\__,_|_| |_|
`;

const style = {
    logo: "color: #22d3ee; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-weight: 700;",
    title: "font-size: 18px; font-weight: 700;",
    subtitle: "color: #71717a; font-size: 12px;",
    heading: "color: #22d3ee; font-weight: 700;",
    body: "",
    muted: "color: #71717a;",
    link: "color: #0e7490; text-decoration: underline;",
    accent: "color: #dc2626; font-weight: 700;",
    badge: "background: #0891b2; color: #fff; padding: 2px 6px; border-radius: 4px; font-weight: 700;",
};

const consoleApi = {
    help() {
        console.log("%cscamatrix.%chelp()%c     this menu", style.muted, style.body, style.muted);
        console.log("%cscamatrix.%cabout()%c    product info", style.muted, style.body, style.muted);
        console.log("%cscamatrix.%ccreator()%c  built by clizSec", style.muted, style.body, style.muted);
        console.log("%cscamatrix.%clinks()%c    social + portfolio", style.muted, style.body, style.muted);
    },
    about() {
        console.log("%c%s", style.title, product.name);
        console.log("%c%s", style.subtitle, product.tagline);
        console.log("%cStatus: %s", style.muted, scamatrix.status);
        console.log("%c%s", style.muted, scamatrix.description);
    },
    creator() {
        console.log("%c%s", style.body, creator.name);
        console.log("%cPortfolio: %c%s", style.muted, style.link, clizsec.canonicalEnglish);
    },
    links() {
        for (const item of social) {
            console.log("%c%s%c  %s", style.heading, item.name.padEnd(10), style.link, item.link);
        }
    },
};

declare global {
    interface Window {
        scamatrix?: typeof consoleApi;
    }
}

let welcomePrinted = false;

function printWelcome() {
    if (welcomePrinted) {
        return;
    }
    welcomePrinted = true;
    console.log("%c%s", style.logo, ASCII_LOGO);
    console.log("%c%s", style.title, product.name);
    console.log("%c%s", style.subtitle, product.tagline);
    console.log("");
    console.log("%cThreat intelligence lookup for the curious.", style.heading);
    console.log("%cNo secrets in the console. Just IOCs, tools, and good hygiene.", style.body);
    console.log("");
    console.log("%cBuilt by %c%s", style.muted, style.link, creator.name);
    console.log("%cPortfolio: %c%s", style.muted, style.link, clizsec.canonicalEnglish);
    console.log("");
    console.log(
        "%cSecurity contact: %c/.well-known/security.txt",
        style.muted,
        style.link,
    );
    console.log(
        "%cTry %cscamatrix.help()%c for commands.",
        style.muted,
        style.body,
        style.muted,
    );
}

export function ScaMatrixConsole() {
    useEffect(() => {
        window.scamatrix = consoleApi;
        printWelcome();

        let dockedNoticeShown = false;

        const onResize = () => {
            if (dockedNoticeShown) {
                return;
            }
            const widthDelta = window.outerWidth - window.innerWidth;
            const heightDelta = window.outerHeight - window.innerHeight;
            if (widthDelta > DOCKED_DEVTOOLS_THRESHOLD_PX || heightDelta > DOCKED_DEVTOOLS_THRESHOLD_PX) {
                dockedNoticeShown = true;
                console.log(
                    "%cDevTools open.%c Scan responsibly.",
                    style.badge,
                    style.body,
                );
            }
        };

        window.addEventListener("resize", onResize);
        onResize();

        return () => {
            window.removeEventListener("resize", onResize);
            delete window.scamatrix;
        };
    }, []);

    return null;
}
